import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Play, Stop, Clock, CaretLeft, CaretRight, Plus, Trash, PencilSimple, ChartBar, CalendarBlank } from "@phosphor-icons/react";
import { toast } from "sonner";

// ---------- Helpers ----------
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const fmtDay = (d) => d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });
const monthLabel = (m) => {
  const [y, mo] = m.split("-");
  return new Date(+y, +mo-1, 1).toLocaleDateString("de-DE", { month: "long", year: "numeric" });
};
const fmtDuration = (min) => {
  const h = Math.floor((min || 0) / 60);
  const m = (min || 0) % 60;
  return `${h}:${String(m).padStart(2,"0")} h`;
};
const startOfWeek = (d) => {
  const x = new Date(d); const day = x.getDay() || 7; x.setDate(x.getDate() - day + 1); return x;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate()+n); return x; };
const liveDuration = (isoStart) => {
  try {
    const ms = new Date() - new Date(isoStart);
    return Math.max(0, Math.floor(ms / 60000));
  } catch { return 0; }
};

// ---------- Konstanten ----------
const DEPARTMENTS = ["Housekeeping", "Küche", "Verwaltung", "Guest Relation", "Hausmeister"];
const DEPARTMENT_COLORS = {
  "Housekeeping":   "bg-blue-100 text-blue-800 ring-1 ring-blue-200",
  "Küche":          "bg-orange-100 text-orange-800 ring-1 ring-orange-200",
  "Verwaltung":     "bg-slate-100 text-slate-800 ring-1 ring-slate-300",
  "Guest Relation": "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200",
  "Hausmeister":    "bg-amber-100 text-amber-800 ring-1 ring-amber-200",
};
const DEPARTMENT_EMOJIS = {
  "Housekeeping": "🧹",
  "Küche": "🍳",
  "Verwaltung": "📋",
  "Guest Relation": "🤝",
  "Hausmeister": "🔧",
};
const DeptBadge = ({ dept }) => {
  if (!dept) return <span className="text-[10px] text-muted-foreground italic">— keine Abteilung —</span>;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${DEPARTMENT_COLORS[dept] || "bg-slate-100 text-slate-700"}`}>
      <span>{DEPARTMENT_EMOJIS[dept] || "·"}</span>{dept}
    </span>
  );
};

// ---------- Live Timer Component ----------
function LiveTimer({ startIso }) {
  const [tick, setTick] = useState(0);
  useEffect(() => { const iv = setInterval(() => setTick(t => t+1), 30000); return () => clearInterval(iv); }, []);
  return <span className="font-mono">{fmtDuration(liveDuration(startIso))}</span>;
}

// ---------- Time Tracker (alle Rollen) ----------
function TimeTracker() {
  const { user } = useAuth();
  const [active, setActive] = useState(null); // {clock_in, ...} oder {}
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // User-Abteilungen aus Profil (via /auth/me — nicht via /users wegen Berechtigung)
  const [myDepts, setMyDepts] = useState(null);
  useEffect(() => {
    api.get("/auth/me")
      .then(r => setMyDepts((r.data?.departments || []).filter(d => DEPARTMENTS.includes(d))))
      .catch(() => setMyDepts([]));
  }, []);

  const load = () => api.get("/time-entries/active").then(r => setActive(r.data || {}));
  useEffect(() => { load(); }, []);

  const start = async (dept) => {
    setBusy(true);
    try {
      await api.post("/time-entries/start", { notes: "", department: dept || null });
      toast.success(dept ? `Dienst gestartet · ${dept}` : "Dienst gestartet");
      setPickerOpen(false);
      await load();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };
  const handleStartClick = () => {
    // Hat User genau EINE Abteilung → direkt starten (Backend fallbackt sowieso, aber UI-feedback schon hier)
    if (myDepts && myDepts.length === 1) {
      return start(myDepts[0]);
    }
    setPickerOpen(true);
  };
  const stop = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/time-entries/stop", { notes: "" });
      toast.success(`Dienst beendet: ${fmtDuration(data.duration_minutes)}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  // Welche Abteilungen zeigen? Wenn User-Abteilungen gesetzt → nur die; sonst alle
  const availableDepts = (myDepts && myDepts.length > 0) ? myDepts : DEPARTMENTS;

  const isRunning = !!active?.clock_in;
  return (
    <div className="msb-card" data-testid="time-tracker">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-2xl bg-primary flex items-center justify-center">
          <Clock size={20} weight="duotone" color="white"/>
        </div>
        <div>
          <h2 className="text-xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Zeiterfassung</h2>
          <p className="text-xs text-muted-foreground">Abteilung wählen → Uhr läuft, bis Sie den Dienst beenden.</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {isRunning ? (
          <>
            <div className="flex-1 min-w-[220px] p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Dienst läuft seit</div>
                <DeptBadge dept={active.department} />
              </div>
              <div className="mt-0.5 text-base font-semibold text-emerald-800">
                {new Date(active.clock_in).toLocaleString("de-DE")}
              </div>
              <div className="text-xs text-emerald-700 mt-0.5"><LiveTimer startIso={active.clock_in}/></div>
            </div>
            <Button onClick={stop} disabled={busy} data-testid="btn-stop" className="rounded-xl bg-red-600 hover:bg-red-700 text-white h-12 px-6">
              <Stop size={18} weight="fill"/><span className="ml-2 font-semibold">Dienst beenden</span>
            </Button>
          </>
        ) : (
          <>
            <div className="flex-1 min-w-[220px] p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-muted-foreground">
              Kein aktiver Dienst. Klicken Sie „Dienst starten", wählen Sie die Abteilung — und die Zeit läuft.
            </div>
            <Button onClick={handleStartClick} disabled={busy} data-testid="btn-start"
                    className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-6">
              <Play size={18} weight="fill"/><span className="ml-2 font-semibold">Dienst starten</span>
            </Button>
          </>
        )}
      </div>
      {pickerOpen && (
        <Dialog open={true} onOpenChange={(o)=>{ if (!o) setPickerOpen(false); }}>
          <DialogContent className="max-w-md" data-testid="dept-picker-dialog">
            <DialogHeader>
              <DialogTitle>In welcher Abteilung starten Sie?</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground -mt-2 mb-2">
              Für die Auswertung je Abteilung — kann bei Bedarf später vom Admin angepasst werden.
            </p>
            <div className="grid grid-cols-2 gap-2.5">
              {availableDepts.map(d => (
                <button key={d}
                        onClick={() => start(d)}
                        disabled={busy}
                        className={`rounded-xl p-4 border-2 border-border hover:border-primary hover:bg-primary/5 transition text-left disabled:opacity-50 ${DEPARTMENT_COLORS[d] ? "" : ""}`}
                        data-testid={`dept-pick-${d}`}>
                  <div className="text-3xl mb-1">{DEPARTMENT_EMOJIS[d]}</div>
                  <div className="font-semibold">{d}</div>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ---------- Shift Grid ----------
function ShiftGrid({ isAdmin, staffUsers, weekStart, setWeekStart }) {
  const [shifts, setShifts] = useState([]);
  const [edit, setEdit] = useState(null); // {date, user_id, ...} oder null

  const days = useMemo(() => Array.from({length: 7}, (_, i) => addDays(weekStart, i)), [weekStart]);
  const from = iso(days[0]);
  const to = iso(days[6]);

  const load = () => api.get("/shifts", { params: { date_from: from, date_to: to } }).then(r => setShifts(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);

  const shiftsOn = (userId, dayIso) => shifts.filter(s => s.user_id === userId && s.date === dayIso);
  const del = async (id) => { await api.delete(`/shifts/${id}`); toast.success("Schicht gelöscht"); load(); };

  return (
    <div className="msb-card" data-testid="shift-grid">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Schichtplan</h2>
          <p className="text-xs text-muted-foreground">
            Kalenderwoche {days[0].toLocaleDateString("de-DE")} – {days[6].toLocaleDateString("de-DE")}
            {isAdmin && " · Klick auf eine Zelle fügt eine Schicht hinzu"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setWeekStart(addDays(weekStart, -7))} className="rounded-lg" data-testid="week-prev">
            <CaretLeft size={14} weight="bold"/>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setWeekStart(startOfWeek(new Date()))} className="rounded-lg" data-testid="week-today">Heute</Button>
          <Button variant="outline" size="sm" onClick={() => setWeekStart(addDays(weekStart, 7))} className="rounded-lg" data-testid="week-next">
            <CaretRight size={14} weight="bold"/>
          </Button>
          {/* Kalender-Icon zum direkten Wochensprung — als <label> für maximale Browser-Kompatibilität */}
          <label
            htmlFor="shift-week-picker"
            className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer transition"
            title="Datum wählen · Woche anspringen"
            data-testid="week-picker-btn"
          >
            <CalendarBlank size={14} weight="bold"/>
            <input
              id="shift-week-picker"
              type="date"
              value={iso(weekStart)}
              onChange={(e) => { if (e.target.value) setWeekStart(startOfWeek(new Date(e.target.value))); }}
              className="sr-only"
            />
          </label>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-muted-foreground">
              <th className="text-left p-3 w-40">Mitarbeiter</th>
              {days.map(d => (
                <th key={iso(d)} className="text-left p-3 whitespace-nowrap">{fmtDay(d)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {staffUsers.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Keine Mitarbeiter angelegt.</td></tr>
            )}
            {staffUsers.map(u => (
              <tr key={u.id} className="border-t border-border">
                <td className="p-3 font-semibold">{u.name || u.email}</td>
                {days.map(d => {
                  const dIso = iso(d);
                  const cellShifts = shiftsOn(u.id, dIso);
                  return (
                    <td key={dIso} className="p-2 align-top border-l border-border/50 min-w-[110px]">
                      <div className="space-y-1">
                        {cellShifts.map(s => (
                          <div key={s.id} className="group text-xs bg-primary/10 text-primary rounded-md px-2 py-1 flex items-center justify-between" data-testid={`shift-${s.id}`}>
                            <span className="font-mono">{s.start_time}–{s.end_time}</span>
                            {isAdmin && (
                              <span className="opacity-0 group-hover:opacity-100 flex items-center gap-1 ml-2">
                                <button onClick={() => setEdit({ ...s })} className="hover:text-slate-800" title="Bearbeiten">
                                  <PencilSimple size={12} weight="bold"/>
                                </button>
                                <button onClick={() => del(s.id)} className="hover:text-red-600" title="Löschen">
                                  <Trash size={12} weight="bold"/>
                                </button>
                              </span>
                            )}
                          </div>
                        ))}
                        {isAdmin && (
                          <button
                            onClick={() => setEdit({ user_id: u.id, date: dIso, start_time: "09:00", end_time: "17:00", notes: "" })}
                            className="w-full text-[11px] text-muted-foreground hover:text-primary border border-dashed border-slate-200 rounded-md py-1 hover:bg-primary/5"
                            data-testid={`add-shift-${u.id}-${dIso}`}
                          >
                            <Plus size={10} weight="bold" className="inline"/> hinzufügen
                          </button>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edit && <ShiftEditDialog value={edit} staffUsers={staffUsers} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }}/>}
    </div>
  );
}

function ShiftEditDialog({ value, staffUsers, onClose, onSaved }) {
  const [d, setD] = useState(value);
  const set = (k, v) => setD(x => ({ ...x, [k]: v }));
  const save = async () => {
    try {
      if (d.id) await api.put(`/shifts/${d.id}`, { user_id: d.user_id, date: d.date, start_time: d.start_time, end_time: d.end_time, notes: d.notes || "" });
      else await api.post("/shifts", { user_id: d.user_id, date: d.date, start_time: d.start_time, end_time: d.end_time, notes: d.notes || "" });
      toast.success("Schicht gespeichert");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  return (
    <Dialog open={true} onOpenChange={(o)=>{if(!o) onClose();}}>
      <DialogContent className="max-w-md" data-testid="shift-edit-dialog">
        <DialogHeader><DialogTitle>{d.id ? "Schicht bearbeiten" : "Neue Schicht"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Mitarbeiter</Label>
            <Select value={d.user_id} onValueChange={(v)=>set("user_id", v)}>
              <SelectTrigger className="rounded-lg mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{staffUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Datum</Label>
            <Input type="date" value={d.date} onChange={(e)=>set("date", e.target.value)} className="rounded-lg mt-1"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Beginn</Label>
              <Input type="time" value={d.start_time} onChange={(e)=>set("start_time", e.target.value)} className="rounded-lg mt-1" data-testid="shift-start"/>
            </div>
            <div>
              <Label className="text-xs">Ende</Label>
              <Input type="time" value={d.end_time} onChange={(e)=>set("end_time", e.target.value)} className="rounded-lg mt-1" data-testid="shift-end"/>
            </div>
          </div>
          <div>
            <Label className="text-xs">Notiz (optional)</Label>
            <Input value={d.notes || ""} onChange={(e)=>set("notes", e.target.value)} className="rounded-lg mt-1" placeholder="z. B. Reinigung EG"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} className="rounded-lg">Abbrechen</Button>
          <Button onClick={save} className="rounded-lg bg-primary text-white" data-testid="shift-save">Speichern</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Manual Time Entry Dialog (Admin) ----------
function ManualEntryDialog({ value, staffUsers, onClose, onSaved }) {
  const [d, setD] = useState(value);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setD(x => ({ ...x, [k]: v }));

  // combine date + time into ISO string
  const toIso = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return "";
    return new Date(`${dateStr}T${timeStr}`).toISOString();
  };
  const save = async () => {
    if (!d.user_id) { toast.error("Bitte Mitarbeiter wählen."); return; }
    if (!d.start_date || !d.start_time) { toast.error("Bitte Start-Datum und -Zeit angeben."); return; }
    const payload = {
      user_id: d.user_id,
      clock_in: toIso(d.start_date, d.start_time),
      clock_out: d.end_date && d.end_time ? toIso(d.end_date, d.end_time) : null,
      department: d.department || null,
      notes_start: d.notes_start || "",
      notes_stop: d.notes_stop || "",
    };
    setBusy(true);
    try {
      if (d.id) await api.put(`/time-entries/${d.id}`, payload);
      else      await api.post("/time-entries", payload);
      toast.success("Zeit gespeichert");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o)=>{if(!o) onClose();}}>
      <DialogContent className="max-w-md" data-testid="manual-entry-dialog">
        <DialogHeader><DialogTitle>{d.id ? "Zeit bearbeiten" : "Zeit manuell nachtragen"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Mitarbeiter</Label>
            <Select value={d.user_id} onValueChange={(v)=>set("user_id", v)}>
              <SelectTrigger className="rounded-lg mt-1" data-testid="me-user"><SelectValue placeholder="Bitte wählen…"/></SelectTrigger>
              <SelectContent>{staffUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Start-Datum</Label>
              <Input type="date" value={d.start_date} onChange={(e)=>set("start_date", e.target.value)} className="rounded-lg mt-1" data-testid="me-start-date"/>
            </div>
            <div>
              <Label className="text-xs">Start-Uhrzeit</Label>
              <Input type="time" value={d.start_time} onChange={(e)=>set("start_time", e.target.value)} className="rounded-lg mt-1" data-testid="me-start-time"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Ende-Datum <span className="text-muted-foreground">(optional)</span></Label>
              <Input type="date" value={d.end_date} onChange={(e)=>set("end_date", e.target.value)} className="rounded-lg mt-1" data-testid="me-end-date"/>
            </div>
            <div>
              <Label className="text-xs">Ende-Uhrzeit</Label>
              <Input type="time" value={d.end_time} onChange={(e)=>set("end_time", e.target.value)} className="rounded-lg mt-1" data-testid="me-end-time"/>
            </div>
          </div>
          <div>
            <Label className="text-xs">Abteilung</Label>
            <Select value={d.department || ""} onValueChange={(v)=>set("department", v === "__none__" ? "" : v)}>
              <SelectTrigger className="rounded-lg mt-1" data-testid="me-department"><SelectValue placeholder="— keine —"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— keine Abteilung —</SelectItem>
                {DEPARTMENTS.map(dp => <SelectItem key={dp} value={dp}>{DEPARTMENT_EMOJIS[dp]} {dp}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Notiz (optional)</Label>
            <Input value={d.notes_start || ""} onChange={(e)=>set("notes_start", e.target.value)} className="rounded-lg mt-1" placeholder="z. B. Nachtrag – Uhr vergessen"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} className="rounded-lg" disabled={busy}>Abbrechen</Button>
          <Button onClick={save} disabled={busy} className="rounded-lg bg-primary text-white" data-testid="me-save">Speichern</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Monthly Report (Admin) ----------
function MonthlyReport({ staffUsers }) {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`);
  const [report, setReport] = useState([]);
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState(null); // dialog value

  const load = () => {
    setBusy(true);
    Promise.all([
      api.get("/time-entries/report", { params: { month } }),
      api.get("/time-entries", { params: { month } }),
    ]).then(([r1, r2]) => { setReport(r1.data); setEntries(r2.data); })
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const openNew = () => setManual({
    user_id: staffUsers[0]?.id || "",
    start_date: iso(new Date()), start_time: "09:00",
    end_date: iso(new Date()),   end_time: "17:00",
    notes_start: "",
  });
  const openEdit = (e) => {
    const ci = new Date(e.clock_in);
    const co = e.clock_out ? new Date(e.clock_out) : null;
    setManual({
      id: e.id, user_id: e.user_id,
      start_date: iso(ci), start_time: `${String(ci.getHours()).padStart(2,"0")}:${String(ci.getMinutes()).padStart(2,"0")}`,
      end_date:   co ? iso(co) : "", end_time: co ? `${String(co.getHours()).padStart(2,"0")}:${String(co.getMinutes()).padStart(2,"0")}` : "",
      department: e.department || "",
      notes_start: e.notes_start || "",
      notes_stop: e.notes_stop || "",
    });
  };
  const del = async (id) => {
    if (!window.confirm("Diesen Eintrag wirklich löschen?")) return;
    try { await api.delete(`/time-entries/${id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const totalMinutes = report.reduce((s, r) => s + (r.total_minutes || 0), 0);

  return (
    <div className="msb-card" data-testid="monthly-report">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center">
            <ChartBar size={20} weight="duotone" className="text-primary"/>
          </div>
          <div>
            <h2 className="text-xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Monatsauswertung</h2>
            <p className="text-xs text-muted-foreground">Summierte Arbeitsstunden pro Mitarbeiter · {monthLabel(month)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input type="month" value={month} onChange={(e)=>setMonth(e.target.value)} className="rounded-lg h-9 w-40" data-testid="report-month"/>
          <Button onClick={openNew} className="rounded-lg bg-primary text-white h-9" data-testid="btn-manual-add">
            <Plus size={14} weight="bold"/><span className="ml-1">Zeit nachtragen</span>
          </Button>
        </div>
      </div>

      {busy ? <div className="text-muted-foreground text-sm py-4">Lade…</div> :
        report.length === 0 && entries.length === 0
          ? <div className="text-muted-foreground text-sm py-8 text-center">Keine abgeschlossenen Dienste in diesem Monat.</div>
          : (
            <>
              {report.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/60">
                      <tr className="text-xs uppercase tracking-widest text-muted-foreground">
                        <th className="text-left p-3">Mitarbeiter</th>
                        <th className="text-left p-3">Einsätze</th>
                        <th className="text-left p-3">Gesamt-Stunden</th>
                        <th className="text-left p-3">Anteil</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.map(r => (
                        <tr key={r.user_id} className="border-t border-border" data-testid={`report-row-${r.user_id}`}>
                          <td className="p-3 font-semibold">{r.user_name}</td>
                          <td className="p-3">{r.entries}</td>
                          <td className="p-3 font-mono">{fmtDuration(r.total_minutes)}</td>
                          <td className="p-3">
                            <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full bg-primary" style={{ width: totalMinutes ? `${(r.total_minutes / totalMinutes) * 100}%` : "0%" }}/>
                            </div>
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-primary/30 bg-primary/5 font-bold">
                        <td className="p-3">Gesamt</td>
                        <td className="p-3">{report.reduce((s,r)=>s+r.entries,0)}</td>
                        <td className="p-3 font-mono">{fmtDuration(totalMinutes)}</td>
                        <td className="p-3"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-6">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-2">Alle Dienste ({entries.length})</div>
                <div className="max-h-72 overflow-y-auto text-xs divide-y divide-dashed divide-border pr-1">
                  {entries.map(e => (
                    <div key={e.id} className="group flex items-center justify-between py-2 px-2 hover:bg-muted/40" data-testid={`entry-${e.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate flex items-center gap-1.5 flex-wrap">
                          {e.user_name || e.user_id.slice(0,8)}
                          <DeptBadge dept={e.department} />
                          {e.manual && <span className="text-[10px] bg-amber-100 text-amber-800 rounded px-1.5 py-0.5">Nachtrag</span>}
                          {e.edited_at && <span className="text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5" title={`Bearbeitet: ${new Date(e.edited_at).toLocaleString("de-DE")}`}>bearbeitet</span>}
                        </div>
                        <div className="text-muted-foreground">{new Date(e.clock_in).toLocaleString("de-DE")}
                          {e.clock_out ? ` – ${new Date(e.clock_out).toLocaleTimeString("de-DE",{hour:"2-digit",minute:"2-digit"})}` : " – läuft"}
                        </div>
                      </div>
                      <div className="ml-3 font-mono text-right">
                        {e.clock_out ? fmtDuration(e.duration_minutes) : <span className="text-emerald-600">läuft</span>}
                      </div>
                      <div className="ml-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                        <button onClick={()=>openEdit(e)} title="Bearbeiten" className="p-1 rounded hover:bg-slate-200" data-testid={`edit-${e.id}`}>
                          <PencilSimple size={14} weight="bold"/>
                        </button>
                        <button onClick={()=>del(e.id)} title="Löschen" className="p-1 rounded hover:bg-red-100 text-red-600" data-testid={`del-${e.id}`}>
                          <Trash size={14} weight="bold"/>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )
      }
      {manual && <ManualEntryDialog value={manual} staffUsers={staffUsers} onClose={()=>setManual(null)} onSaved={()=>{setManual(null); load();}}/>}
    </div>
  );
}

// ---------- Staff Personal Overview ----------
function StaffOverview({ userId }) {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`);
  const [entries, setEntries] = useState([]);
  useEffect(() => {
    api.get("/time-entries", { params: { month } }).then(r => setEntries(r.data));
  }, [month, userId]);
  const totalMin = entries.reduce((s,e)=>s+(e.duration_minutes||0), 0);
  return (
    <div className="msb-card" data-testid="staff-overview">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Meine Dienste</h2>
          <p className="text-xs text-muted-foreground">{monthLabel(month)} · Gesamt <b>{fmtDuration(totalMin)}</b></p>
        </div>
        <Input type="month" value={month} onChange={(e)=>setMonth(e.target.value)} className="rounded-lg h-9 w-40"/>
      </div>
      {entries.length === 0
        ? <div className="text-muted-foreground text-sm py-6 text-center">Keine Dienste in diesem Monat.</div>
        : <div className="space-y-1 text-sm max-h-72 overflow-y-auto pr-1">
            {entries.map(e => (
              <div key={e.id} className="flex items-center justify-between py-1.5 px-2 border-b border-dashed border-border">
                <div>
                  <div className="font-semibold">{new Date(e.clock_in).toLocaleDateString("de-DE",{weekday:"short",day:"2-digit",month:"2-digit",year:"numeric"})}</div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(e.clock_in).toLocaleTimeString("de-DE",{hour:"2-digit",minute:"2-digit"})}
                    {e.clock_out ? ` – ${new Date(e.clock_out).toLocaleTimeString("de-DE",{hour:"2-digit",minute:"2-digit"})}` : " – läuft"}
                  </div>
                </div>
                <div className="font-mono">
                  {e.clock_out ? fmtDuration(e.duration_minutes) : <span className="text-emerald-600">läuft</span>}
                </div>
              </div>
            ))}
          </div>}
    </div>
  );
}

// ---------- Department Report (Admin) ----------
function DepartmentReport() {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setBusy(true);
    api.get("/time-entries/report-by-department", { params: { month } })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const totalMin = data?.total_minutes || 0;

  return (
    <div className="msb-card" data-testid="department-report">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center">
            <ChartBar size={20} weight="duotone" className="text-primary"/>
          </div>
          <div>
            <h2 className="text-xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Auswertung pro Abteilung</h2>
            <p className="text-xs text-muted-foreground">Wo entsteht wie viel Arbeit · {monthLabel(month)}</p>
          </div>
        </div>
        <Input type="month" value={month} onChange={(e)=>setMonth(e.target.value)} className="rounded-lg h-9 w-40" data-testid="dept-report-month"/>
      </div>

      {busy && <div className="text-muted-foreground text-sm py-6 text-center">Lade…</div>}
      {!busy && (!data || (data.departments || []).length === 0) && (
        <div className="text-muted-foreground text-sm py-8 text-center">Keine abgeschlossenen Dienste in diesem Monat.</div>
      )}
      {!busy && data && (data.departments || []).length > 0 && (
        <>
          {/* Horizontaler Balken-Vergleich */}
          <div className="space-y-2 mb-5">
            {data.departments.map(dept => {
              const pct = totalMin ? Math.round((dept.total_minutes / totalMin) * 100) : 0;
              return (
                <div key={dept.department} className="group" data-testid={`dept-bar-${dept.department}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{DEPARTMENT_EMOJIS[dept.department] || "·"}</span>
                      <span className="font-semibold">{dept.department}</span>
                      <span className="text-[11px] text-muted-foreground">({dept.total_entries} Einsätze)</span>
                    </div>
                    <div className="font-mono font-semibold">{fmtDuration(dept.total_minutes)} · {pct}%</div>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${DEPARTMENT_COLORS[dept.department]?.split(" ")[0]?.replace("100","500") || "bg-primary"} transition-all duration-500`}
                         style={{ width: `${pct}%` }}/>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pro Abteilung: welche Mitarbeiter wie viel beigetragen haben */}
          <div className="grid gap-3 md:grid-cols-2">
            {data.departments.map(dept => (
              <div key={dept.department} className="border border-border rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <span className="text-base">{DEPARTMENT_EMOJIS[dept.department] || "·"}</span>
                    <span className="font-semibold text-sm">{dept.department}</span>
                  </div>
                  <span className="text-xs font-mono text-primary font-semibold">{fmtDuration(dept.total_minutes)}</span>
                </div>
                <div className="space-y-1 text-xs">
                  {dept.users.map(u => (
                    <div key={u.user_id} className="flex items-center justify-between py-1 border-b border-dashed border-border last:border-0">
                      <span className="truncate">{u.user_name}</span>
                      <span className="font-mono text-muted-foreground">{fmtDuration(u.minutes)} <span className="opacity-60">({u.entries})</span></span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5 pt-3 border-t border-dashed border-border flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Gesamt im Monat</span>
            <span className="font-mono font-bold text-primary">{fmtDuration(totalMin)}</span>
          </div>
        </>
      )}
    </div>
  );
}

// ---------- Page ----------
export default function Schichtplan() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [users, setUsers] = useState([]);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));

  useEffect(() => {
    if (isAdmin) {
      api.get("/users").then(r => {
        // Reihenfolge: Admins zuerst (nach created_at ASC → Ivan, dann Carolin), dann Staff (Larissa)
        const sorted = [...r.data].sort((a, b) => {
          if (a.role !== b.role) return a.role === "admin" ? -1 : 1;
          return (a.created_at || "").localeCompare(b.created_at || "");
        });
        setUsers(sorted);
      });
    } else {
      // Staff: nur sich selbst im Grid anzeigen
      setUsers(user ? [{ id: user.id, name: user.name, email: user.email, role: user.role }] : []);
    }
  }, [isAdmin, user]);

  const staffUsers = users; // Alle User (Admins + Staff) im Grid sichtbar

  return (
    <div className="space-y-6" data-testid="schichtplan-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight" style={{fontFamily:"'Work Sans',sans-serif"}}>Schichtplan &amp; Zeiterfassung</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          {isAdmin ? "Diensteinteilung planen und Arbeitszeiten auswerten" : "Ihr persönlicher Schichtplan und Dienstzeiten"}
        </p>
      </div>

      <TimeTracker />
      <ShiftGrid isAdmin={isAdmin} staffUsers={staffUsers} weekStart={weekStart} setWeekStart={setWeekStart}/>
      {isAdmin ? (
        <>
          <DepartmentReport />
          <MonthlyReport staffUsers={staffUsers}/>
        </>
      ) : (
        <StaffOverview userId={user?.id}/>
      )}
    </div>
  );
}
